<template>
  <div class="ceiling-tracker">
    <div v-if="isLoading" class="loading-screen">
      <div class="loading-spinner"></div>
      <p class="loading-text">Загрузка данных...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="orders.length === 0" class="empty-state">
      <div class="logo-container">
        <div class="logo">
          <img src="@/assets/images/mp1.svg" alt="Мир Потолков" />
        </div>
      </div>
      <div class="empty-content">
        <div class="empty-icon">📦</div>
        <h2 class="empty-title">Заказы не найдены</h2>
        <p class="empty-text">На данный момент у вас нет активных заказов</p>
      </div>
    </div>

    <div v-else>
      <div class="logo-container">
        <div class="logo">
          <img src="@/assets/images/mp1.svg" alt="Мир Потолков" />
        </div>
      </div>

      <div class="order-carousel-wrapper">
        <div 
          ref="orderCarousel" 
          class="order-carousel"
          @scroll="handleScroll"
        >
          <button
            v-for="order in orders"
            :key="order.id"
            :class="['order-btn', { active: order.id === selectedOrderId }]"
            @click="selectOrder(order.id)"
          >
            {{ order.number }}
          </button>
        </div>
      </div>

      <div v-if="selectedOrder" class="order-info-card">
        <h2 class="order-title">
          Заказ №{{ selectedOrder.number?.includes('-')
            ? selectedOrder.number.split('-')[0]
            : selectedOrder.number
          }}
          ({{ selectedOrder.totalCeilings }}
          {{ declension(selectedOrder.totalCeilings, ['потолок', 'потолка', 'потолков']) }})
        </h2>

        <div class="chart-section">
          <div class="donut-chart-container">
            <svg class="donut-chart" viewBox="0 0 200 200">
              <circle
                v-for="segment in chartSegments"
                :key="segment.color"
                class="donut-segment"
                cx="100"
                cy="100"
                r="80"
                fill="none"
                :stroke="segment.color"
                :stroke-width="30"
                :stroke-dasharray="`${segment.length} ${circumference}`"
                :stroke-dashoffset="segment.offset"
                transform="rotate(-90 100 100)"
              />
            </svg>
            <div class="donut-center">
              <div class="percentage">{{ selectedOrder.completedPercentage }}%</div>
              <div class="label">прогресс</div>
            </div>
          </div>

          <div class="legend">
            <div
              v-for="status in selectedOrder.statuses"
              :key="status.name"
              class="legend-item"
            >
              <span class="legend-color" :style="{ backgroundColor: status.color }"></span>
              <span class="legend-text">{{ status.name }} — {{ status.count }}</span>
            </div>
          </div>
        </div>

        <p class="time-estimate">{{ selectedOrder.timeEstimate }}</p>
      </div>

      <div v-if="selectedOrder" :key="selectedOrderId" class="ceilings-progress-card">
        <h2 class="card-title">Прогресс по потолкам</h2>

        <div class="ceilings-list">
          <div
            v-for="ceiling in selectedOrder.ceilings"
            :key="ceiling.id"
            class="ceiling-item"
          >
            <div class="ceiling-indicator" :style="{ backgroundColor: ceiling.statusColor }"></div>
            
            <div class="ceiling-info">
              <div class="ceiling-name">{{ ceiling.name }}</div>
              <div 
                class="ceiling-stage" 
                :class="{ 'status-text-error': ceiling.status === 'not_confirmed' }"
              >
                {{ getStatusText(ceiling.status) }}
              </div>
            </div>
            
            <div class="ceiling-status">
              <div v-if="ceiling.status === 'not_confirmed'" class="status-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="#FF4D4F" stroke-width="2"/>
                  <path d="M9 9L15 15M15 9L9 15" stroke="#FF4D4F" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </div>

              <div v-else-if="ceiling.status === 'queued'" class="status-icon status-queued">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="#9E9E9E" stroke-width="2"/>
                  <path d="M12 7V12L15 15" stroke="#9E9E9E" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </div>
              
              <div v-else-if="ceiling.status === 'cutting'" class="status-icon">
                <div :ref="el => { if (el) lottieRefs[ceiling.id] = el }" style="width:55px; height:55px;"></div>
              </div>
              
              <div v-else-if="ceiling.status === 'harpoon'" class="status-timer">
                {{ formatTime(ceiling.timeRemaining) }}
              </div>
              
              <div v-else-if="ceiling.status === 'unpacked'" class="status-icon status-unpacked">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="3" width="18" height="18" rx="4" stroke="#FF6B6B" stroke-width="2"/>
                  <path d="M8 8L16 16M16 8L8 16" stroke="#FF6B6B" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </div>
              
              <div v-else-if="ceiling.status === 'packed'" class="status-completed">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="3" width="18" height="18" rx="4" stroke="#FF8C42" stroke-width="2"/>
                  <path d="M7 12L10 15L17 8" stroke="#FF8C42" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import axios from 'axios'
import lottie from 'lottie-web'
import scissors from '@/assets/animations/scissors.json'

// Состояния
const selectedOrderId = ref(null)
const timerInterval = ref(null)
const updateInterval = ref(null)
const orders = ref([])
const isLoading = ref(true)
const clientId = ref(null)
const orderCarousel = ref(null)
const lottieRefs = ref({}) 
const lottieInstances = ref({}) 

// Вычисляемые свойства
const selectedOrder = computed(() => {
  return orders.value.find(order => order.id === selectedOrderId.value) || null
})

const circumference = computed(() => 2 * Math.PI * 80)

const chartSegments = computed(() => {
  if (!selectedOrder.value) return []
  const segments = []
  let currentOffset = 0

  selectedOrder.value.statuses.forEach(status => {
    const percentage = (status.count / selectedOrder.value.totalCeilings) * 100
    const length = (percentage / 100) * circumference.value
    segments.push({
      color: status.color,
      length,
      offset: -currentOffset
    })
    currentOffset += length
  })

  return segments
})

// Функции
const declension = (num, forms) => {
  const n = Math.abs(num) % 100
  const n1 = n % 10
  if (n > 10 && n < 20) return forms[2]
  if (n1 === 1) return forms[0]
  if (n1 >= 2 && n1 <= 4) return forms[1]
  return forms[2]
}

const getClientIdFromUrl = () => {
  const pathParts = window.location.pathname.split('/').filter(p => p)
  if (pathParts.length > 0) return pathParts[pathParts.length - 1]
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('id') || urlParams.get('numz') || '155517'
}

const selectOrder = (orderId) => {
  selectedOrderId.value = orderId
}

const buildOrderQuery = (value) => {
  if (!value) return 'id=155517'
  if (/^\d+$/.test(value) || value === 'test') return `id=${value}`
  return `numz=${encodeURIComponent(value)}`
}

const getStatusText = (status) => {
  const statuses = {
    not_confirmed: 'Не подтвержден',
    queued: 'В очереди',
    cutting: 'На раскрое',
    harpoon: 'На гарпуне',
    unpacked: 'Не упакован',
    packed: 'Упакован'
  }
  return statuses[status] || ''
}

const formatTime = (seconds) => {
  if (seconds <= 0) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const startTimers = () => {
  timerInterval.value = setInterval(() => {
    let hasChanges = false
    orders.value.forEach(order => {
      order.ceilings.forEach(ceiling => {
        if (ceiling.status === 'harpoon' && ceiling.timeRemaining > 0) {
          ceiling.timeRemaining--
          hasChanges = true
          if (ceiling.timeRemaining <= 0) {
            const hasPackScan = ceiling.scans.some(scan => 
              scan.typename?.toUpperCase() === 'PACK'
            )
            ceiling.status = hasPackScan ? 'packed' : 'unpacked'
            ceiling.statusColor = hasPackScan ? '#FF8C42' : '#FF6B6B'
          }
        }
      })
    })
    if (hasChanges) orders.value = [...orders.value]
  }, 1000)
}

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))

const autoScrollCarousel = async () => {
  const carousel = orderCarousel.value
  if (!carousel) return
  await sleep(500)
  carousel.scrollTo({ left: carousel.scrollWidth, behavior: 'smooth' })
  await sleep(700)
  carousel.scrollTo({ left: 0, behavior: 'smooth' })
}

const fetchOrders = async () => {
  const rawValue = getClientIdFromUrl()
  const query = buildOrderQuery(rawValue)
  const url = `https://mpapi.premside.uz/api/order?${query}`

  try {
    const response = await axios.get(url, {
      timeout: 15000,
      headers: { Accept: 'application/json' }
    })

    if (response.data.status === 'ok' && Array.isArray(response.data.data)) {
      const transformedOrders = response.data.data
        .map(transformOrderData)
        .filter(Boolean)

      orders.value = transformedOrders
      if (!selectedOrderId.value && orders.value.length > 0) {
        selectedOrderId.value = orders.value[0].id
      }
    }
  } catch (err) {
    console.error(err)
  } finally {
    isLoading.value = false
  }
}

const transformOrderData = (orderData) => {
  const ceilings = (orderData.potolki || []).map((potolok) => {
    const garpScan = getGarpScan(potolok.Scans)
    const perimeter = garpScan ? garpScan.Perimetr : (potolok.CountPoint * 2.5)
    
    const ceiling = {
      id: potolok.IDPotolok,
      name: `Потолок №${potolok.NumPotolok}`,
      countPoints: potolok.CountPoint,
      perimeter,
      status: '',
      statusColor: '',
      timeRemaining: 0,
      dateCreate: potolok.DateCreate,
      scans: potolok.Scans || [],
      garpScan
    }

    // ЛОГИКА: Если has_IDOper_6 === false, ставим статус "Не подтвержден"
    if (potolok.has_IDOper_6 === false) {
      ceiling.status = 'not_confirmed'
      ceiling.statusColor = '#FF4D4F'
      return ceiling
    }

    const detectedStatus = detectCeilingStatus(potolok)

    if (detectedStatus === 'queued') {
      ceiling.status = 'queued'
      ceiling.statusColor = '#9E9E9E'
    } else if (detectedStatus === 'cutting') {
      ceiling.status = 'cutting'
      ceiling.statusColor = '#5B4DFF'
    } else if (detectedStatus === 'harpoon') {
      const remaining = calculateRemainingTime(ceiling)
      if (remaining <= 0) {
        const hasPackScan = ceiling.scans.some(scan => scan.typename?.toUpperCase() === 'PACK')
        ceiling.status = hasPackScan ? 'packed' : 'unpacked'
        ceiling.statusColor = hasPackScan ? '#FF8C42' : '#FF6B6B'
      } else {
        ceiling.status = 'harpoon'
        ceiling.statusColor = '#C44DFF'
        ceiling.timeRemaining = remaining
      }
    } else if (detectedStatus === 'packed') {
      ceiling.status = 'packed'
      ceiling.statusColor = '#FF8C42'
    }
    return ceiling
  })

  const order = {
    id: orderData.IDZakaz,
    number: orderData.NumZakaz,
    totalCeilings: orderData.count_ceiling,
    ceilings,
    completedPercentage: 0,
    timeEstimate: '',
    statuses: []
  }

  recalculateOrderStats(order)
  return order
}

const getGarpScan = (scans) => {
  return (scans || []).find(scan => scan.typename?.toUpperCase() === 'GARP') || null
}

const detectCeilingStatus = (potolok) => {
  const scans = potolok.Scans || []
  if (scans.length === 0) return 'queued'
  const lastType = (potolok.last_typename || '').toUpperCase().trim()
  if (lastType === 'PACK') return 'packed'
  if (lastType === 'GARP') return 'harpoon'
  if (lastType === 'RASKROY') return 'cutting'
  return 'queued'
}

const calculateRemainingTime = (ceiling) => {
  const garp = ceiling.garpScan
  if (!garp?.end_timestamp) return 0
  const endSeconds = Number(garp.end_timestamp)
  const nowSecondsAlmaty = Math.floor(Date.now() / 1000) + 5 * 3600
  return Math.max(0, endSeconds - nowSecondsAlmaty)
}

const recalculateOrderStats = (order) => {
  let queued = 0, cutting = 0, harpoon = 0, unpacked = 0, packed = 0, notConfirmed = 0
  let totalHarpoonTime = 0

  order.ceilings.forEach(ceiling => {
    if (ceiling.status === 'not_confirmed') notConfirmed++
    else if (ceiling.status === 'queued') queued++
    else if (ceiling.status === 'cutting') cutting++
    else if (ceiling.status === 'harpoon') {
      harpoon++
      totalHarpoonTime += ceiling.timeRemaining
    }
    else if (ceiling.status === 'unpacked') unpacked++
    else if (ceiling.status === 'packed') packed++
  })

  // ЛЕГЕНДА: Обратный порядок
  order.statuses = [
    { name: 'В очереди', count: queued, color: '#9E9E9E' },
    { name: 'Раскрой', count: cutting, color: '#5B4DFF' },
    { name: 'Гарпун', count: harpoon, color: '#C44DFF' },
    { name: 'Не упакован', count: unpacked, color: '#FF6B6B' },
    { name: 'Упакован', count: packed, color: '#FF8C42' }
  ]

  // Если есть неподтвержденные, добавляем их в список легенды
  if (notConfirmed > 0) {
    order.statuses.push({ name: 'Не подтвержден', count: notConfirmed, color: '#FF4D4F' })
  }

  let totalProgress = 0
  order.ceilings.forEach(ceiling => {
    if (ceiling.status === 'packed') totalProgress += 100
    else if (ceiling.status === 'unpacked') totalProgress += 75
    else if (ceiling.status === 'harpoon') totalProgress += 50
    else if (ceiling.status === 'cutting') totalProgress += 25
  })
  order.completedPercentage = Math.round(totalProgress / order.totalCeilings)
  
  // Текст оценки
  if (notConfirmed > 0) {
    order.timeEstimate = `⚠️ ${notConfirmed} ${pluralize(notConfirmed, 'потолок', 'потолка', 'потолков')} не ${notConfirmed === 1 ? 'подтвержден' : 'подтверждены'}`
  } else if (packed === order.totalCeilings) {
    order.timeEstimate = '🎉 Все потолки готовы!'
  } else if (harpoon > 0) {
    const mins = Math.floor(totalHarpoonTime / 60)
    order.timeEstimate = `⚡ Примерно ${mins} мин. до полной готовности`
  }
}

const pluralize = (number, one, two, five) => {
  const n = Math.abs(number) % 100
  if (n >= 5 && n <= 20) return five
  const mod10 = n % 10
  if (mod10 === 1) return one
  if (mod10 >= 2 && mod10 <= 4) return two
  return five
}

const handleScroll = () => {}

const initLottieAnimations = () => {
  Object.values(lottieInstances.value).forEach(i => i?.destroy())
  lottieInstances.value = {}
  lottieRefs.value = {}
  nextTick(() => {
    if (!selectedOrder.value) return
    selectedOrder.value.ceilings.forEach(ceiling => {
      if (ceiling.status === 'cutting') {
        const container = lottieRefs.value[ceiling.id]
        if (container) {
          lottieInstances.value[ceiling.id] = lottie.loadAnimation({
            container, renderer: 'svg', loop: true, autoplay: true, animationData: scissors
          })
        }
      }
    })
  })
}

watch(selectedOrderId, () => nextTick(initLottieAnimations))

onMounted(async () => {
  clientId.value = getClientIdFromUrl()
  await fetchOrders()
  autoScrollCarousel()
  startTimers()
  setTimeout(initLottieAnimations, 100)
  updateInterval.value = setInterval(async () => {
    await fetchOrders()
    initLottieAnimations()
  }, 30000)
})

onBeforeUnmount(() => {
  clearInterval(updateInterval.value)
  clearInterval(timerInterval.value)
  Object.values(lottieInstances.value).forEach(i => i?.destroy())
})
</script>



<style scoped>
.status-icon svg {
  transition: transform 0.3s ease;
}
.status-text-error {
  color: #FF4D4F !important;
  font-weight: 700;
}
/* Все ваши стили остаются без изменений */
.loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: linear-gradient(180deg, #2d2d44 0%, #1a1a2e 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-spinner {
  width: 60px;
  height: 60px;
  border: 6px solid rgba(91, 77, 255, 0.2);
  border-top-color: #5B4DFF;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  margin-top: 20px;
  color: #fff;
  font-size: 18px;
  font-weight: 500;
}

.time-estimate {
  text-align: center;
  color: #666;
  font-size: 14px;
  margin: 0;
  white-space: pre-line;
  line-height: 1.6;
}

* {
  box-sizing: border-box;
}

.ceiling-tracker {
  max-width: 575px;
  margin: 0 auto;
  padding:15px;
  padding-top: 5px;
  background: linear-gradient(180deg, #2d2d44 0%, #1a1a2e 100%);
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.logo-container {
  background: #f5f5f5;
  border-radius: 20px;
  padding: 10px 15px;
  /* padding-left: 20px; */
  margin-bottom: 10px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 15px;
}

.logo img {
  /* width: 80px; */
  height: 60px;
  object-fit: contain;
}

.logo-text h1 {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  color: #b8976d;
  letter-spacing: 2px;
}

.logo-text p {
  margin: 5px 0 0;
  font-size: 12px;
  color: #999;
  letter-spacing: 1px;
}

.order-carousel-wrapper {
  margin-bottom: 10px;
  position: relative;
}

.order-carousel {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  /* padding-bottom: 5px; */
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.order-carousel::-webkit-scrollbar {
  display: none;
}

.order-btn {
  flex-shrink: 0;
  min-width: 110px;
  padding: 15px 20px;
  background: #fff;
  border: none;
  border-radius: 30px;
  font-size: 18px;
  font-weight: 600;
  color: #2d2d44;
  cursor: pointer;
  transition: all 0.3s ease;
}

.order-btn.active {
  background: #5B4DFF;
  color: #fff;
}

.order-info-card {
  background: #f5f5f5;
  border-radius: 20px;
  padding: 30px;
  margin-bottom: 10px;
}

.order-title {
  margin: 0 0 30px;
  font-size: 22px;
  font-weight: 600;
  color: #2d2d44;
  text-align: center;
}

.chart-section {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
}

.donut-chart-container {
  position: relative;
  width: 160px;
  height: 160px;
  flex-shrink: 0;
}

.donut-chart {
  width: 100%;
  height: 100%;
}

.donut-segment {
  transition: stroke-dasharray 0.5s ease, stroke-dashoffset 0.5s ease;
}

.donut-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.percentage {
  font-size: 30px;
  font-weight: 700;
  color: #2d2d44;
  line-height: 1;
}

.label {
  font-size: 14px;
  color: #666;
  margin-top: 5px;
}

.legend {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.legend-color {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  flex-shrink: 0;
}

.legend-text {
  font-size: 15px;
  color: #2d2d44;
  font-weight: 500;
  line-height: 1.2;
}

.ceilings-progress-card {
  background: #f5f5f5;
  border-radius: 20px;
  padding: 15px;
}

.card-title {
  margin: 0 0 20px;
  font-size: 22px;
  font-weight: 600;
  color: #2d2d44;
  text-align: center;
}

.ceilings-list {
  background: #fff;
  border-radius: 15px;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ceiling-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background: #fafafa;
  border-radius: 12px;
}

.ceiling-indicator {
  width: 6px;
  height: 55px;
  border-radius: 3px;
  flex-shrink: 0;
}

.ceiling-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ceiling-name {
  font-size: 18px;
  font-weight: 600;
  color: #2d2d44;
  line-height: 1.2;
}

.ceiling-stage {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.ceiling-status {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.status-icon svg,
.status-completed svg {
  display: block;
}

.status-timer {
  background: #C44DFF;
  color: #fff;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 700;
  min-width: 70px;
  text-align: center;
  font-family: 'Courier New', monospace;
}

@media (max-width: 400px) {
  .ceiling-tracker {
    padding: 15px;
  }

  .order-btn {
    font-size: 16px;
    padding: 12px 16px;
    min-width: 100px;
  }

  .ceiling-name {
    font-size: 16px;
  }

  .ceiling-stage {
    font-size: 13px;
  }

  .status-timer {
    font-size: 16px;
    padding: 6px 12px;
    min-width: 65px;
  }

  .legend-text {
    font-size: 14px;
  }
}
/* Добавить в секцию <style scoped> */

/* Стили для иконки "В очереди" */
.status-queued svg {
  display: block;
}

/* Стили для иконки "Не упакован" */
.status-unpacked svg {
  display: block;
}

/* Анимация пульсации для статуса "Не упакован" */
.status-unpacked {
  animation: pulse-unpacked 2s ease-in-out infinite;
}

@keyframes pulse-unpacked {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

/* Empty State */
.empty-state {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.empty-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.empty-icon {
  font-size: 80px;
  margin-bottom: 20px;
  opacity: 0.5;
}

.empty-title {
  font-size: 24px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 12px 0;
}

.empty-text {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  max-width: 400px;
  line-height: 1.5;
}

</style>